import { useState, FormEvent, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useAuth } from '../../contexts/AuthContext';
import { REGISTER_MUTATION } from '../../graphql/mutations';
import { PLATFORM_SETTINGS_QUERY } from '../../graphql/queries';
import BrandMark from '../../components/common/BrandMark';
import styles from './Register.module.css';
import Icon from '../../components/common/Icon';

const industries = [
  { value: 'Engineering', label: 'Engineering' },
  { value: 'Surveying', label: 'Surveying' },
  { value: 'Architecture', label: 'Architecture' },
  { value: 'Construction', label: 'Construction' },
  { value: 'UrbanPlanning', label: 'Urban Planning' },
];

const getPasswordStrength = (password: string) => {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score;
};

const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
const strengthColors = ['', 'bg-error', 'bg-orange-400', 'bg-yellow-400', 'bg-green-500'];

const Register = () => {
  // Arriving from a project-transfer email: ?transfer=<token>&email=<recipient>.
  // The email is prepopulated and locked so the created account matches the
  // transfer; after registration the transfer is applied server-side and the
  // user lands on the transfer page to see the result.
  const [searchParams] = useSearchParams();
  const transferToken = searchParams.get('transfer') || undefined;
  const transferEmail = searchParams.get('email') || '';

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: transferEmail,
    password: '',
    confirmPassword: '',
    companyName: '',
    industry: '',
  });
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const { data: settingsData, loading: settingsLoading } = useQuery(PLATFORM_SETTINGS_QUERY, { fetchPolicy: 'cache-first' });

  useEffect(() => {
    if (!settingsLoading && settingsData?.platformSettings?.usersAllowedToRegister === false) {
      navigate('/login', { replace: true });
    }
  }, [settingsData, settingsLoading, navigate]);

  const [registerMutation, { loading }] = useMutation(REGISTER_MUTATION, {
    onCompleted: (data) => {
      login(data.register.token, data.register.user);
      navigate(transferToken ? `/transfer/${transferToken}` : '/dashboard');
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const passwordStrength = getPasswordStrength(form.password);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    const { confirmPassword, ...input } = form;
    registerMutation({
      variables: {
        input: {
          ...input,
          industry: input.industry || undefined,
        },
        transferToken,
      },
    });
  };

  const updateForm = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <div className={styles.container}>
      <div className={styles.bgBlob} />

      <main className={styles.grid}>
        <div className="flex flex-col justify-center space-y-8 pr-0 md:pr-12">
          <div className="flex items-center space-x-3">
            <BrandMark size={40} decorative />
            <h1 className="font-headline text-2xl font-bold tracking-tight text-on-background">Infrio</h1>
          </div>
          <div className="space-y-4">
            <h2 className="font-headline text-4xl md:text-5xl font-bold text-on-background leading-tight">
              Engineer the <span className="text-primary">Future</span> of Geospatial Data.
            </h2>
            <p className="text-on-surface-variant text-lg leading-relaxed max-w-md">
              Join thousands of architects and surveyors using architectural-depth precision to map the world.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {([
              { icon: 'layers', title: '2D & 3D Synthesis', desc: 'Seamlessly transition between CAD drawings and BIM models.' },
              { icon: 'cloud_done', title: 'Centralized Point Clouds', desc: 'Host and share multi-gigabit datasets with sub-millimeter accuracy.' },
            ] as const).map((f) => (
              <div key={f.title} className="bg-surface-container-low p-5 rounded-xl flex items-start space-x-4">
                <Icon name={f.icon} className="text-primary" />
                <div>
                  <h4 className="font-headline font-bold text-sm">{f.title}</h4>
                  <p className="text-xs text-on-surface-variant">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.card}>
          <div className="mb-8">
            <h3 className="font-headline text-2xl font-bold text-on-background mb-1">Create Account</h3>
            <p className="text-sm text-on-surface-variant">Start your 14-day premium trial today.</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-error-container rounded-lg flex items-center gap-2 text-sm text-on-error-container">
              <Icon name="error" className="text-base" />
              {error}
            </div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold font-label uppercase tracking-wider text-on-surface-variant" htmlFor="register-first-name">First Name</label>
                <input id="register-first-name" className="input-field" placeholder="John" type="text" required value={form.firstName} onChange={(e) => updateForm('firstName', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold font-label uppercase tracking-wider text-on-surface-variant" htmlFor="register-last-name">Last Name</label>
                <input id="register-last-name" className="input-field" placeholder="Doe" type="text" required value={form.lastName} onChange={(e) => updateForm('lastName', e.target.value)} />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold font-label uppercase tracking-wider text-on-surface-variant" htmlFor="register-company">Company Name</label>
              <input id="register-company" className="input-field" placeholder="e.g. Skyline Engineering" type="text" required value={form.companyName} onChange={(e) => updateForm('companyName', e.target.value)} />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold font-label uppercase tracking-wider text-on-surface-variant" htmlFor="register-industry">Industry</label>
              <select id="register-industry" className="input-field" value={form.industry} onChange={(e) => updateForm('industry', e.target.value)}>
                <option value="">Select industry...</option>
                {industries.map((ind) => (
                  <option key={ind.value} value={ind.value}>{ind.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold font-label uppercase tracking-wider text-on-surface-variant" htmlFor="register-email">Email Address</label>
              <input id="register-email" className="input-field" placeholder="work@infrio.cloud" type="email" required value={form.email} readOnly={!!(transferToken && transferEmail)} onChange={(e) => updateForm('email', e.target.value)} />
              {transferToken && transferEmail && (
                <p className="text-[10px] text-on-surface-variant">A project transfer is waiting for this email address.</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold font-label uppercase tracking-wider text-on-surface-variant" htmlFor="register-password">Password</label>
                <input id="register-password" className="input-field" placeholder="••••••••" type="password" required value={form.password} onChange={(e) => updateForm('password', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold font-label uppercase tracking-wider text-on-surface-variant" htmlFor="register-confirm-password">Repeat Password</label>
                <input id="register-confirm-password" className="input-field" placeholder="••••••••" type="password" required value={form.confirmPassword} onChange={(e) => updateForm('confirmPassword', e.target.value)} />
              </div>
            </div>

            {form.password && (
              <div className="space-y-2 pt-1">
                <div className="flex justify-between items-center text-[10px] font-bold font-label uppercase tracking-tight">
                  <span className="text-on-surface-variant">Password Strength</span>
                  <span className="text-primary">{strengthLabels[passwordStrength]}</span>
                </div>
                <div className="flex gap-1 h-1">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className={`${styles.strengthBar} ${i <= passwordStrength ? strengthColors[passwordStrength] : 'bg-surface-container-highest'}`}
                    />
                  ))}
                </div>
                <p className="text-[10px] text-on-surface-variant/70 italic">At least 8 characters. Mixing letters, numbers and symbols makes it stronger.</p>
              </div>
            )}

            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary bg-gradient-to-r from-primary to-primary-container text-on-primary font-bold py-4 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Icon name="refresh" className="text-lg animate-spin" />
                    Creating account...
                  </>
                ) : (
                  <>
                    <span>Register</span>
                    <Icon name="arrow_forward" />
                  </>
                )}
              </button>
            </div>

            <div className="pt-6 text-center">
              <p className="text-xs text-on-surface-variant">
                Already have an account?{' '}
                <Link to="/login" className="text-primary font-bold hover:underline">Sign In</Link>
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-outline-variant/10 flex items-center justify-between">
              <p className="text-[10px] text-on-surface-variant leading-tight max-w-[200px]">
                By registering, you agree to our <a className="underline" href="#">Terms</a> and <a className="underline" href="#">Privacy Policy</a>.
              </p>
              <div className="flex space-x-2">
                {(['shield', 'verified_user'] as const).map((icon) => (
                  <div key={icon} className="w-6 h-6 rounded-full bg-surface-container flex items-center justify-center opacity-60">
                    <Icon name={icon} className="text-xs" />
                  </div>
                ))}
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default Register;
